import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import {
  Home, BookOpen, TrendingUp, Users,
  Settings, LogOut, Menu, MessageSquare, Sparkles, LayoutGrid, Lock,
} from 'lucide-react';
import OrgLogoWatermark from 'components/OrgLogoWatermark';


/* ─── AVATAR ─────────────────────────────────────────────────────────────── */
const AVATAR_COLORS = [
  'var(--brand-secondary, #FF7300)','#0d9488','#dc2626','#d97706','#2563eb',
  '#db2777','#059669','#7c3aed','#c2410c','#0891b2',
];
const getInitials = (name = '') =>
  name.split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('') || '?';

const SidebarAvatar = ({ user, size = 34 }) => {
  const [err, setErr] = useState(false);
  const color = AVATAR_COLORS[Math.abs((user?.id || '').charCodeAt(0) || 0) % AVATAR_COLORS.length];
  const ring = 'rgb(var(--brand-secondary-rgb, 255 115 0) / 0.9)';
  const glow = 'rgb(var(--brand-secondary-rgb, 255 115 0) / 0.7)';
  if (user?.avatar_url && !err) {
    return (
      <img
        src={`${user.avatar_url.split('?')[0]}?t=${Date.now()}`}
        alt={user?.name}
        onError={() => setErr(true)}
        style={{
          width: size, height: size, borderRadius: '50%',
          objectFit: 'cover', flexShrink: 0,
          border: `1.5px solid ${ring}`,
          boxShadow: `0 0 14px ${glow}`,
        }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: `radial-gradient(circle at 30% 30%, ${color}, ${color})`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontSize: size * 0.36, color: '#fff', flexShrink: 0,
      border: `1.5px solid ${ring}`,
      boxShadow: `0 0 14px ${glow}`,
    }}>
      {getInitials(user?.name)}
    </div>
  );
};

/* ─── STYLES ─────────────────────────────────────────────────────────────────
   Todo el sidebar sale de las variables de marca de ThemeProvider:
     --brand-secondary-rgb  → neón del marco, ítem activo, logo
     --brand-primary-rgb    → degradado del logo
     --brand-accent-rgb     → insignia "AI Tutor"
     --brand-bg-rgb         → fondo del sidebar
     --brand-text-rgb       → textos e iconos
   Los valores tras la coma son los de antes, para cuando no hay organización.
──────────────────────────────────────────────────────────────────────────── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800&display=swap');
  
  html, body { 
    margin: 0 !important; 
    padding: 0 !important; 
    width: 100%; 
    height: 100%; 
    overflow: hidden; 
  }
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'DM Sans', sans-serif; }

  @keyframes fadeIn    { from{opacity:0} to{opacity:1} }
  @keyframes pulse     { 0%,100%{opacity:1} 50%{opacity:0.3} }
  
  @keyframes neonPulse {
  0%,100% {
    box-shadow:
      0 0 calc(14px * var(--neon)) rgb(var(--brand-secondary-rgb, 255 115 0) / calc(0.75 * var(--neon))),
      0 0 calc(38px * var(--neon)) rgb(var(--brand-secondary-rgb, 255 115 0) / calc(0.45 * var(--neon))),
      0 0 calc(80px * var(--neon)) rgb(var(--brand-secondary-rgb, 255 115 0) / calc(0.2 * var(--neon))),
      inset 0 0 calc(18px * var(--neon)) rgb(var(--brand-secondary-rgb, 255 115 0) / calc(0.18 * var(--neon)));
    border-color: rgb(var(--brand-secondary-rgb, 255 115 0) / calc(0.85 * var(--neon)));
  }
  50% {
    box-shadow:
      0 0 calc(26px * var(--neon)) rgb(var(--brand-secondary-rgb, 255 115 0) / calc(1 * var(--neon))),
      0 0 calc(60px * var(--neon)) rgb(var(--brand-secondary-rgb, 255 115 0) / calc(0.7 * var(--neon))),
      0 0 calc(120px * var(--neon)) rgb(var(--brand-secondary-rgb, 255 115 0) / calc(0.35 * var(--neon))),
      inset 0 0 calc(28px * var(--neon)) rgb(var(--brand-secondary-rgb, 255 115 0) / calc(0.3 * var(--neon)));
    border-color: rgb(var(--brand-secondary-rgb, 255 115 0) / calc(1 * var(--neon)));
  }
}

  @keyframes aiBadgePulse {
    0%,100% {
      box-shadow: 0 0 14px rgb(var(--brand-accent-rgb, 132 204 22) / 0.7), 0 0 30px rgb(var(--brand-accent-rgb, 132 204 22) / 0.35);
      border-color: rgb(var(--brand-accent-rgb, 132 204 22) / 0.8);
    }
    50% {
      box-shadow: 0 0 24px rgb(var(--brand-accent-rgb, 132 204 22) / 1), 0 0 50px rgb(var(--brand-accent-rgb, 132 204 22) / 0.6);
      border-color: rgb(var(--brand-accent-rgb, 132 204 22) / 1);
    }
  }

  .ml-root {
    display: flex;
    width: 100vw;
    height: 100vh;
    overflow: hidden;
    position: relative;
    font-family: var(--org-font, 'DM Sans', sans-serif);
    background-color: var(--brand-bg, #060810);
    background-image:
      linear-gradient(rgb(var(--brand-bg-rgb, 5 6 18) / var(--brand-overlay, 0)),
                      rgb(var(--brand-bg-rgb, 5 6 18) / var(--brand-overlay, 0))),
      var(--brand-bg-image, url('/bg-dashboard.png'));
    background-size: cover;
    background-position: center top;
    background-attachment: fixed;
  }

  .ml-sidebar {
    width: 195px;
    flex-shrink: 0;
    position: fixed;
    top: 12px;
    left: 12px;
    height: calc(100vh - 24px);
    display: flex;
    flex-direction: column;
    padding: 18px 0 14px;
    z-index: 20;
    border-radius: 20px;
    background: rgb(var(--brand-bg-rgb, 6 5 20) / 0.78);
    backdrop-filter: blur(24px) saturate(180%);
    -webkit-backdrop-filter: blur(24px) saturate(180%);
    border: 2px solid rgb(var(--brand-secondary-rgb, 255 115 0) / 0.9);
    animation: neonPulse 4s ease-in-out infinite;
    overflow: hidden;
  }

  .ml-sidebar::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgb(var(--brand-secondary-rgb, 255 115 0) / 1), transparent);
    box-shadow: 0 0 12px rgb(var(--brand-secondary-rgb, 255 115 0) / 0.9);
  }

  .ml-logo {
    display: flex; align-items: center; gap: 9px;
    padding: 0 14px 14px;
    border-bottom: 1px solid rgb(var(--brand-text-rgb, 255 255 255) / 0.08);
    margin-bottom: 4px;
  }
  .ml-logo-icon {
    width: 32px; height: 32px;
    background: linear-gradient(135deg, var(--brand-primary, #FFA700), var(--brand-secondary, #FF7300));
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 12px; font-weight: 800; color: #fff; flex-shrink: 0;
    box-shadow:
      0 0 18px rgb(var(--brand-secondary-rgb, 255 115 0) / 0.95),
      0 0 40px rgb(var(--brand-secondary-rgb, 255 115 0) / 0.55);
    border: 1px solid rgb(var(--brand-secondary-rgb, 255 115 0) / 0.9);
    letter-spacing: -0.5px;
  }
  .ml-logo-title {
    font-size: 13px; font-weight: 800;
    color: var(--brand-text, #fff); letter-spacing: -0.2px;
  }
  .ml-logo-sub {
    font-size: 9px; color: rgb(var(--brand-text-rgb, 255 255 255) / 0.55); margin-top: 1px; letter-spacing: 0.06em;
  }

  .ml-user-block {
    display: flex; align-items: center; gap: 9px;
    padding: 10px 14px 12px;
    border-bottom: 1px solid rgb(var(--brand-text-rgb, 255 255 255) / 0.08);
    margin-bottom: 6px;
  }
  .ml-user-info { flex: 1; min-width: 0; }
  .ml-user-name {
    font-size: 12px; font-weight: 700;
    color: var(--brand-text, rgba(255,255,255,0.88));
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .ml-user-role {
    font-size: 10px; color: rgb(var(--brand-text-rgb, 255 255 255) / 0.55); margin-top: 1px;
  }

  .ml-nav {
    flex: 1;
    display: flex; flex-direction: column;
    gap: 3px; padding: 0 10px;
    overflow-y: auto;
  }
  .ml-nav::-webkit-scrollbar { display: none; }

  .ml-nav-item {
    display: flex; align-items: center; gap: 9px;
    padding: 9px 11px; border-radius: 10px;
    color: rgb(var(--brand-text-rgb, 255 255 255) / 0.7);
    font-size: 13px; font-weight: 500;
    cursor: pointer; transition: all 0.18s;
    text-decoration: none; position: relative;
    border: 1px solid rgb(var(--brand-secondary-rgb, 255 115 0) / 0.12);
  }
  .ml-nav-item:hover {
    background: rgb(var(--brand-secondary-rgb, 255 115 0) / 0.22);
    color: var(--brand-text, rgba(255,255,255,0.95));
    border-color: rgb(var(--brand-secondary-rgb, 255 115 0) / 0.7);
    box-shadow: 0 0 16px rgb(var(--brand-secondary-rgb, 255 115 0) / 0.45);
  }
  .ml-nav-item.active {
  background: rgb(var(--brand-secondary-rgb, 255 115 0) / calc(0.3 * var(--neon)));
  border-color: rgb(var(--brand-secondary-rgb, 255 115 0) / calc(1 * var(--neon)));
  box-shadow:
    0 0 calc(18px * var(--neon)) rgb(var(--brand-secondary-rgb, 255 115 0) / calc(0.8 * var(--neon))),
    0 0 calc(36px * var(--neon)) rgb(var(--brand-secondary-rgb, 255 115 0) / calc(0.4 * var(--neon)));
  text-shadow: 0 0 calc(10px * var(--neon)) rgb(var(--brand-secondary-rgb, 255 115 0) / calc(0.8 * var(--neon)));
}
  .ml-nav-item svg { flex-shrink: 0; }

  .ml-divider {
    height: 1px;
    background: rgb(var(--brand-text-rgb, 255 255 255) / 0.08);
    margin: 6px 10px;
  }

  .ml-ai-badge {
    margin: 6px 10px 4px;
    display: flex; align-items: center; gap: 7px;
    padding: 9px 12px; border-radius: 12px;
    border: 1.5px solid rgb(var(--brand-accent-rgb, 132 204 22) / 0.8);
    background: rgb(var(--brand-accent-rgb, 132 204 22) / 0.14);
    cursor: pointer; transition: all 0.2s;
    text-decoration: none;
    animation: aiBadgePulse 2.8s ease-in-out infinite;
  }
  .ml-ai-badge:hover { background: rgb(var(--brand-accent-rgb, 132 204 22) / 0.25); }
  .ml-ai-dot {
    width: 6px; height: 6px;
    background: var(--brand-accent, #84cc16); border-radius: 50%;
    box-shadow:
      0 0 8px var(--brand-accent, #84cc16),
      0 0 16px rgb(var(--brand-accent-rgb, 132 204 22) / 0.7);
    animation: pulse 1.6s ease-in-out infinite;
    flex-shrink: 0;
  }
  .ml-ai-text {
    font-size: 11px; font-weight: 700;
    color: var(--brand-accent, #a3e635); letter-spacing: 0.05em;
    text-shadow: 0 0 8px rgb(var(--brand-accent-rgb, 132 204 22) / 0.9);
  }

  .ml-footer { padding: 0 10px; }
  .ml-footer-btn {
    display: flex; align-items: center; gap: 9px;
    padding: 9px 11px; border-radius: 10px;
    color: rgb(var(--brand-text-rgb, 255 255 255) / 0.6);
    font-size: 13px; font-weight: 500;
    cursor: pointer; transition: all 0.15s;
    border: none; background: transparent;
    font-family: 'DM Sans', sans-serif; width: 100%;
    text-decoration: none;
  }
  .ml-footer-btn:hover {
    background: rgb(var(--brand-text-rgb, 255 255 255) / 0.08);
    color: var(--brand-text, rgba(255,255,255,0.85));
  }
  .ml-footer-btn.logout:hover {
    background: rgba(239,68,68,0.1);
    color: #f87171;
  }

  .ml-main {
    flex: 1;
    margin-left: 219px;
    width: calc(100vw - 219px);
    display: flex;
    flex-direction: column;
    min-width: 0;
    height: 100vh;
    overflow-x: hidden;
    overflow-y: auto;
    position: relative;
    z-index: 1;
  }

  .ml-mobile-header {
    display: none;
    background: rgb(var(--brand-bg-rgb, 6 5 20) / 0.9);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-bottom: 1px solid rgb(var(--brand-secondary-rgb, 255 115 0) / 0.5);
    padding: 12px 18px;
    align-items: center; justify-content: space-between;
    position: sticky; top: 0; z-index: 15;
  }
  .ml-mobile-title { font-size: 14px; font-weight: 700; color: var(--brand-text, #fff); }
  .ml-mobile-btn {
    background: none; border: none; cursor: pointer;
    color: rgb(var(--brand-text-rgb, 255 255 255) / 0.8); padding: 4px;
  }

  .ml-overlay {
    display: none; position: fixed; inset: 0;
    background: rgba(0,0,0,0.7); z-index: 19;
    border: none; padding: 0; width: 100%; height: 100%; cursor: default;  
  }

  @media (max-width: 768px) {
    .ml-sidebar {
      top: 0; left: 0;
      height: 100vh;
      border-radius: 0 20px 20px 0;
      transform: translateX(-110%);
      transition: transform 0.27s ease;
    }
    .ml-sidebar.open { transform: translateX(0); }
    .ml-mobile-header { display: flex; }
    .ml-overlay.open { display: block; }
    .ml-main { margin-left: 0; width: 100%; }
  }

  .t-main      { color: var(--brand-text, #fff); }
  .t-dim       { color: rgb(var(--brand-text-rgb, 255 255 255) / .65); }
  .t-faint     { color: rgb(var(--brand-text-rgb, 255 255 255) / .45); }
  .t-primary   { color: var(--brand-primary, #00FFFF); }
  .t-secondary { color: var(--brand-secondary, #A855F7); }
  .t-accent    { color: var(--brand-accent, #CCFF00); }
`;

const MainLayout = ({ children, hideSidebar = false }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  useEffect(() => {
    if (!sidebarOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setSidebarOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [sidebarOpen]);
  const handleLogout = () => { logout(); };

  const navigation = [];
  if (user?.role === 'superadmin') {
    navigation.push(
      { name: 'Central de Comando', href: '/superadmin', icon: Home },
      { name: 'Gestión de Módulos', href: '/superadmin/modules', icon: BookOpen },
      { name: 'Configuración', href: '/superadmin/settings', icon: Settings }
    );
  } else if (user?.role === 'admin') {
    navigation.push(
      { name: 'Mi Empresa', href: '/admin', icon: Home },
      { name: 'Configuración', href: '/settings', icon: Settings },
    );
  } else {
    navigation.push(
      { name: 'Inicio', href: '/dashboard', icon: Home },
      { name: 'Módulos', href: '/catalog', icon: LayoutGrid },
      { name: 'Mis Módulos', href: '/my-modules', icon: BookOpen },
      { name: 'Progreso', href: '/progress', icon: TrendingUp },
      { name: 'Configuración', href: '/settings', icon: Settings },
    );
  }

  return (
    <div className="ml-root">
      <style>{STYLES}</style>

      <OrgLogoWatermark />
      {!hideSidebar && (
        <>
          <button type="button" tabIndex={-1} aria-label="Cerrar menú"
            className={`ml-overlay ${sidebarOpen ? 'open' : ''}`}
            onClick={() => setSidebarOpen(false)} />
          <aside className={`ml-sidebar ${sidebarOpen ? 'open' : ''}`}>
            <div className="ml-logo">
              <div className="ml-logo-icon">AI</div>
              <div>
                <div className="ml-logo-title">Learning</div>
                <div className="ml-logo-sub">Platform</div>
              </div>
            </div>

            <div className="ml-user-block">
              <SidebarAvatar user={user} />
              <div className="ml-user-info">
                <div className="ml-user-name">{user?.name || 'Usuario'}</div>
                <div className="ml-user-role">
                  {user?.role === 'superadmin' ? 'Super Admin' : user?.role === 'admin' ? 'Administrador' : 'Estudiante'}
                </div>
              </div>
            </div>

            <nav className="ml-nav">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`ml-nav-item ${isActive ? 'active' : ''}`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <item.icon size={18} />
                    {item.name}
                  </Link>
                );
              })}
            </nav>

            <div className="ml-divider" />

            {user?.role === 'user' && (
  user?.features?.ai_tutor ? (
    <Link to="/ai-tutor" className="ml-ai-badge">
      <div className="ml-ai-dot" />
      <span className="ml-ai-text">AI TUTOR ACTIVO</span>
    </Link>
  ) : (
    <button type="button" className="ml-ai-badge"
      style={{ width: 'calc(100% - 20px)', fontFamily: 'inherit' }}
      onClick={() => setShowUpgrade(true)}>
      <div className="ml-ai-dot" />
      <span className="ml-ai-text">AI TUTOR · PLUS</span>
      <Lock size={12} style={{ marginLeft: 'auto', color: 'var(--brand-accent, #a3e635)' }} />
    </button>
  )
)}

            <div className="ml-footer">
              <button onClick={handleLogout} className="ml-footer-btn logout">
                <LogOut size={18} />
                Cerrar Sesión
              </button>
            </div>
          </aside>
        </>
      )}

      <main className="ml-main" style={hideSidebar ? { marginLeft: 0, paddingLeft: 0 } : {}}>
        <header className="ml-mobile-header">
          <button className="ml-mobile-btn" onClick={() => setSidebarOpen(true)}>
            <Menu size={24} />
          </button>
          <div className="ml-mobile-title">Learning Platform</div>
          <SidebarAvatar user={user} size={30} />
        </header>
        {children}
        {showUpgrade && (
  <div onClick={() => setShowUpgrade(false)} style={{
    position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(2,4,10,0.85)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
  }}>
    <div onClick={e => e.stopPropagation()} style={{
      width: '100%', maxWidth: 420, padding: 28, borderRadius: 24, textAlign: 'center',
      background: '#05070f', border: '1px solid var(--brand-accent, #ffa600)',
    }}>
      <Sparkles size={32} color="var(--brand-accent, #ffa600)" style={{ margin: '0 auto 12px' }} />
      <h2 style={{ color: '#fff', margin: '0 0 8px', fontSize: 20, fontWeight: 800 }}>Pásate al plan PLUS</h2>
      <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, lineHeight: 1.6, margin: '0 0 20px' }}>
        El tutor IA no está incluido en tu plan actual. Habla con el administrador de tu empresa para activarlo.
      </p>
      <button onClick={() => setShowUpgrade(false)} style={{
        padding: '10px 32px', borderRadius: 12, fontWeight: 900, fontSize: 12, cursor: 'pointer',
        background: 'var(--brand-accent, #ffa600)', color: '#000', border: 'none',
      }}>
        ENTENDIDO
      </button>
    </div>
  </div>
)}
      </main>
    </div>
  );
};

export default MainLayout;